import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  AlertTriangle, Check, Info, Loader2, Plus, Search, Trash2, Undo2, Upload, X, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DocumentTransactionsService from "./DocumentTransactions.service";
import { ACCESS_LIST_PAGE_SIZE, MAX_ACCESS_BATCH_SIZE, MAX_SELECTION_SIZE } from "./Documents.types";
import type { AccessMode, DocumentTemplate, TemplateRole } from "./Documents.types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: DocumentTemplate | null;
  onChanged: () => void;
};

// A User ID / E_id is required — never empty/whitespace-only.
function cleanId(raw: unknown): string {
  return String(raw ?? "").trim();
}

type BulkStep = "idle" | "column-select" | "preview";

function RoleAccessTable({
  templateId, role, initialMode, initialCount, onApplied,
}: {
  templateId: string;
  role: TemplateRole;
  initialMode: AccessMode;
  initialCount: number;
  onApplied: (template: DocumentTemplate) => void;
}) {
  const service = useRef(new DocumentTransactionsService());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const label = role === "student" ? "Students" : "Faculty";

  // ── Authoritative (server-confirmed) state — only ever updated after a
  // successful Apply, never optimistically. ──────────────────────────────
  const [ mode, setMode ] = useState<AccessMode>(initialMode);
  const [ grantedCount, setGrantedCount ] = useState(initialCount);

  // ── Staged (pending) changes — nothing here has touched the server yet. ──
  const [ pendingMode, setPendingMode ] = useState<AccessMode | null>(null);
  const [ pendingGrants, setPendingGrants ] = useState<Set<string>>(new Set());
  const [ pendingRevokes, setPendingRevokes ] = useState<Set<string>>(new Set());
  const [ pendingClearAll, setPendingClearAll ] = useState(false);

  const effectiveMode = pendingMode ?? mode;
  // Editing only makes sense once Restricted Access is (or is about to be)
  // on — while Open, the list still shows (it's real data), but every
  // control that would mutate it is disabled rather than hidden.
  const controlsDisabled = effectiveMode !== "restricted";

  // ── Paginated table (server-side; RDB has no OFFSET, so pages are a
  // cursor stack — one entry per page visited, so "Back" doesn't refetch
  // page 1's cursor from scratch). Always fetches regardless of mode — a
  // leftover list from a previous Restricted period is still real data. ──
  const [ cursorHistory, setCursorHistory ] = useState<(string | null)[]>([ null ]);
  const [ pageIndex, setPageIndex ] = useState(0);
  const [ rows, setRows ] = useState<string[]>([]);
  const [ hasMore, setHasMore ] = useState(false);
  const [ nextCursor, setNextCursor ] = useState<string | null>(null);
  const [ rowsLoading, setRowsLoading ] = useState(false);
  const [ refreshTick, setRefreshTick ] = useState(0);
  const [ selected, setSelected ] = useState<Set<string>>(new Set());

  // ── Exact-match search — a role's granted list has at most one entry per
  // identifier, so this is a direct lookup, not a fuzzy filter. ───────────
  const [ searchInput, setSearchInput ] = useState("");
  const [ searching, setSearching ] = useState(false);
  const [ searchResult, setSearchResult ] = useState<{ id: string; found: boolean } | null>(null);

  const [ identifier, setIdentifier ] = useState("");
  const [ notice, setNotice ] = useState<string | null>(null);
  const [ applying, setApplying ] = useState(false);
  const [ clearAllConfirmOpen, setClearAllConfirmOpen ] = useState(false);
  const [ applyConfirmOpen, setApplyConfirmOpen ] = useState(false);

  const [ bulkStep, setBulkStep ] = useState<BulkStep>("idle");
  const [ sheetRows, setSheetRows ] = useState<unknown[][]>([]);
  const [ columnIndex, setColumnIndex ] = useState<number | null>(null);
  const [ previewIds, setPreviewIds ] = useState<string[]>([]);
  const [ bulkParsing, setBulkParsing ] = useState(false);

  // ── Bulk Delete — same shape as Bulk Add (file → column → preview →
  // stage), just staging revokes instead of grants. Shares the same
  // combined MAX_ACCESS_BATCH_SIZE ceiling as everything else staged for
  // this role, since both ultimately land in the same single Apply call. ──
  const deleteFileInputRef = useRef<HTMLInputElement>(null);
  const [ bulkDeleteStep, setBulkDeleteStep ] = useState<BulkStep>("idle");
  const [ deleteSheetRows, setDeleteSheetRows ] = useState<unknown[][]>([]);
  const [ deleteColumnIndex, setDeleteColumnIndex ] = useState<number | null>(null);
  const [ previewDeleteIds, setPreviewDeleteIds ] = useState<string[]>([]);
  const [ bulkDeleteParsing, setBulkDeleteParsing ] = useState(false);

  useEffect(() => {
    if (searchResult) return;
    let cancelled = false;
    setRowsLoading(true);
    service.current.fetchAccessList(templateId, role, {
      limit: ACCESS_LIST_PAGE_SIZE,
      cursor: cursorHistory[ pageIndex ],
    }).then((res) => {
      if (cancelled) return;
      if (res.success) {
        setRows(res.ids);
        setHasMore(res.hasMore);
        setNextCursor(res.nextCursor);
      }
    }).finally(() => { if (!cancelled) setRowsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ pageIndex, refreshTick, searchResult ]);

  const refreshTable = () => {
    setCursorHistory([ null ]);
    setPageIndex(0);
    setSelected(new Set());
    setRefreshTick((t) => t + 1);
  };

  const goNext = () => {
    if (!hasMore || nextCursor === null) return;
    setCursorHistory((h) => [ ...h.slice(0, pageIndex + 1), nextCursor ]);
    setPageIndex((i) => i + 1);
  };

  const goPrev = () => {
    if (pageIndex === 0) return;
    setPageIndex((i) => i - 1);
  };

  // ── Staging helpers — grant/revoke on the same id always cancel each
  // other out, so the pending set never holds a contradictory pair. ───────
  const stageGrant = (id: string) => {
    setPendingRevokes((prev) => { const next = new Set(prev); next.delete(id); return next; });
    setPendingGrants((prev) => new Set(prev).add(id));
  };

  const stageRevoke = (id: string) => {
    setPendingGrants((prev) => { const next = new Set(prev); next.delete(id); return next; });
    setPendingRevokes((prev) => new Set(prev).add(id));
  };

  const unstageGrant = (id: string) => {
    setPendingGrants((prev) => { const next = new Set(prev); next.delete(id); return next; });
  };

  const unstageRevoke = (id: string) => {
    setPendingRevokes((prev) => { const next = new Set(prev); next.delete(id); return next; });
  };

  const toggleMode = (restricted: boolean) => {
    const next: AccessMode = restricted ? "restricted" : "open";
    setPendingMode(next === mode ? null : next);
  };

  const runSearch = async () => {
    const id = cleanId(searchInput);
    if (!id) { setSearchResult(null); return; }
    setSearching(true);
    try {
      const res = await service.current.fetchAccessList(templateId, role, { search: id });
      setSearchResult({ id, found: res.success && res.ids.length > 0 });
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchResult(null);
  };

  const addIndividual = () => {
    const id = cleanId(identifier);
    if (!id) return;
    stageGrant(id);
    setIdentifier("");
    setNotice(null);
  };

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      if (prev.has(id)) {
        const next = new Set(prev);
        next.delete(id);
        return next;
      }
      if (prev.size >= MAX_SELECTION_SIZE) {
        setNotice(`You can select up to ${MAX_SELECTION_SIZE} at a time — remove some from the selection first, or use Bulk Delete for larger removals.`);
        return prev;
      }
      return new Set(prev).add(id);
    });
  };

  const removeSelected = () => {
    selected.forEach((id) => stageRevoke(id));
    setSelected(new Set());
  };

  const clearSelection = () => setSelected(new Set());

  const resetBulk = () => {
    setBulkStep("idle");
    setSheetRows([]);
    setColumnIndex(null);
    setPreviewIds([]);
  };

  const handleBulkFile = async (file: File) => {
    setBulkParsing(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[ workbook.SheetNames[ 0 ] ];
      const parsedRows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as unknown[][];
      if (parsedRows.length < 1) { setNotice("That file has no rows."); return; }
      setSheetRows(parsedRows);
      setColumnIndex(0);
      setBulkStep("column-select");
      setNotice(null);
    } finally {
      setBulkParsing(false);
    }
  };

  const columnHeaders = (sheetRows[ 0 ] || []).map((cell, i) => cleanId(cell) || `Column ${i + 1}`);

  const buildPreview = (colIndex: number) => {
    const ids: string[] = [];
    const seen = new Set<string>();
    sheetRows.slice(1).forEach((row) => {
      const id = cleanId(row?.[ colIndex ]);
      if (id && !seen.has(id)) { seen.add(id); ids.push(id); }
    });
    setPreviewIds(ids);
    setBulkStep("preview");
  };

  const stageBulk = () => {
    const roomLeft = Math.max(0, MAX_ACCESS_BATCH_SIZE - (pendingGrants.size + pendingRevokes.size));
    const toStage = previewIds.slice(0, roomLeft);
    const truncated = previewIds.length > toStage.length;
    toStage.forEach((id) => stageGrant(id));
    setNotice(
      truncated
        ? `Staged ${toStage.length} of ${previewIds.length} IDs — a single Apply can include at most ${MAX_ACCESS_BATCH_SIZE} changes. Apply this batch, then upload the rest.`
        : `Staged ${toStage.length} new ${label.toLowerCase()} — review below and Apply when ready.`
    );
    resetBulk();
  };

  const resetBulkDelete = () => {
    setBulkDeleteStep("idle");
    setDeleteSheetRows([]);
    setDeleteColumnIndex(null);
    setPreviewDeleteIds([]);
  };

  const handleBulkDeleteFile = async (file: File) => {
    setBulkDeleteParsing(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[ workbook.SheetNames[ 0 ] ];
      const parsedRows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as unknown[][];
      if (parsedRows.length < 1) { setNotice("That file has no rows."); return; }
      setDeleteSheetRows(parsedRows);
      setDeleteColumnIndex(0);
      setBulkDeleteStep("column-select");
      setNotice(null);
    } finally {
      setBulkDeleteParsing(false);
    }
  };

  const deleteColumnHeaders = (deleteSheetRows[ 0 ] || []).map((cell, i) => cleanId(cell) || `Column ${i + 1}`);

  const buildDeletePreview = (colIndex: number) => {
    const ids: string[] = [];
    const seen = new Set<string>();
    deleteSheetRows.slice(1).forEach((row) => {
      const id = cleanId(row?.[ colIndex ]);
      if (id && !seen.has(id)) { seen.add(id); ids.push(id); }
    });
    setPreviewDeleteIds(ids);
    setBulkDeleteStep("preview");
  };

  const stageBulkDelete = () => {
    const roomLeft = Math.max(0, MAX_ACCESS_BATCH_SIZE - (pendingGrants.size + pendingRevokes.size));
    const toStage = previewDeleteIds.slice(0, roomLeft);
    const truncated = previewDeleteIds.length > toStage.length;
    toStage.forEach((id) => stageRevoke(id));
    setNotice(
      truncated
        ? `Staged ${toStage.length} of ${previewDeleteIds.length} removals — a single Apply can include at most ${MAX_ACCESS_BATCH_SIZE} changes. Apply this batch, then upload the rest.`
        : `Staged ${toStage.length} ${label.toLowerCase()} for removal — review below and Apply when ready.`
    );
    resetBulkDelete();
  };

  const pendingCount = pendingGrants.size + pendingRevokes.size;
  const hasPendingChanges = pendingMode !== null || pendingCount > 0 || pendingClearAll;

  const confirmClearAll = () => {
    setPendingClearAll(true);
    setPendingGrants(new Set());
    setPendingRevokes(new Set());
    setClearAllConfirmOpen(false);
  };

  const undoClearAll = () => setPendingClearAll(false);

  const applyChanges = async () => {
    setApplying(true);
    try {
      const result = await service.current.applyAccessChanges(templateId, role, {
        mode: pendingMode ?? undefined,
        grantIds: pendingClearAll ? [] : Array.from(pendingGrants),
        revokeIds: pendingClearAll ? [] : Array.from(pendingRevokes),
        clearAll: pendingClearAll,
      });
      if (result.success && result.template) {
        const applied = result.template.access[ role ];
        setMode(applied.mode);
        setGrantedCount(applied.allowedIdsCount);
        setPendingMode(null);
        setPendingGrants(new Set());
        setPendingRevokes(new Set());
        setPendingClearAll(false);
        setApplyConfirmOpen(false);
        clearSearch();
        refreshTable();
        onApplied(result.template);
      }
    } finally {
      setApplying(false);
    }
  };

  const atBatchLimit = pendingCount >= MAX_ACCESS_BATCH_SIZE;
  const showEmptyPlaceholder = !rowsLoading && rows.length === 0 && grantedCount === 0;

  return (
    <div className="flex flex-col gap-3 py-2">
      {/* Row 1 — Restricted Access toggle | Search */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="flex items-center justify-between gap-4 rounded-xl border border-primary/20 bg-primary/5 p-4 lg:w-[400px] shrink-0">
          <div>
            <p className="text-sm font-bold text-primary">Restricted Access</p>
            <p className="text-[11px] text-primary/70">
              {effectiveMode === "open"
                ? `Any ${label.toLowerCase()} can self-serve download this template.`
                : `Only ${label.toLowerCase()} explicitly granted below can download it (${grantedCount} currently granted).`}
            </p>
            {pendingMode !== null && (
              <p className="mt-1 text-[11px] font-bold text-amber-700">
                Pending: {mode === "open" ? "Restricted" : "Open"} → {pendingMode === "open" ? "Open" : "Restricted"} (not applied yet)
              </p>
            )}
          </div>
          <Switch checked={effectiveMode === "restricted"} onCheckedChange={toggleMode} disabled={applying} />
        </div>

        <div className="flex-1 space-y-1.5">
          <Label className="text-[11px] font-bold text-muted-foreground">Look Up an Existing ID</Label>
          <div className="flex gap-2">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !controlsDisabled && runSearch()}
              placeholder="Exact User ID / E_id"
              disabled={controlsDisabled}
            />
            <Button variant="outline" onClick={runSearch} disabled={controlsDisabled || searching || !cleanId(searchInput)} className="gap-1.5 text-xs font-bold shrink-0">
              {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            </Button>
            {searchResult && (
              <Button variant="ghost" size="icon" onClick={clearSearch} className="shrink-0"><X className="h-3.5 w-3.5" /></Button>
            )}
          </div>
        </div>
      </div>

      {/* Row 2 — Add individual | Bulk upload */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="flex-1 space-y-1.5">
          <Label className="text-[11px] font-bold text-muted-foreground">Add Individual — User ID / E_id</Label>
          <div className="flex gap-2">
            <Input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !controlsDisabled && addIndividual()}
              placeholder={role === "student" ? "e.g. STU-2024-001" : "e.g. EMP-2024-042"}
              disabled={controlsDisabled || atBatchLimit}
            />
            <Button onClick={addIndividual} disabled={controlsDisabled || atBatchLimit || !cleanId(identifier)} className="gap-1.5 text-xs font-bold shrink-0">
              <Plus className="h-3.5 w-3.5" /> Stage
            </Button>
          </div>
        </div>

        <div className="flex-1 space-y-1.5">
          <Label className="text-[11px] font-bold text-muted-foreground">Bulk Add (CSV / Excel)</Label>
          {bulkStep === "idle" && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[ 0 ]; if (f) handleBulkFile(f); e.target.value = ""; }}
              />
              <Button variant="outline" className="gap-1.5 text-xs font-bold w-full" onClick={() => fileInputRef.current?.click()} disabled={controlsDisabled || bulkParsing || atBatchLimit}>
                {bulkParsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Upload List
              </Button>
            </>
          )}
          {bulkStep === "column-select" && (
            <div className="space-y-2 rounded-lg border border-border/60 p-3">
              <Label className="text-[11px] font-bold text-muted-foreground">Which column is the User ID / E_id?</Label>
              <Select value={String(columnIndex)} onValueChange={(v) => setColumnIndex(Number(v))}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {columnHeaders.map((h, i) => <SelectItem key={i} value={String(i)}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-2 pt-1">
                <Button variant="ghost" size="sm" className="text-xs font-bold" onClick={resetBulk}>Cancel</Button>
                <Button size="sm" className="text-xs font-bold" onClick={() => columnIndex !== null && buildPreview(columnIndex)}>
                  Preview
                </Button>
              </div>
            </div>
          )}
          {bulkStep === "preview" && (
            <div className="space-y-2 rounded-lg border border-border/60 p-3">
              <Label className="text-[11px] font-bold text-muted-foreground">
                Found {previewIds.length} unique ID{previewIds.length !== 1 ? "s" : ""}
              </Label>
              <div className="max-h-24 overflow-y-auto flex flex-wrap gap-1">
                {previewIds.slice(0, 40).map((id) => (
                  <Badge key={id} variant="secondary" className="font-mono text-[10px]">{id}</Badge>
                ))}
                {previewIds.length > 40 && (
                  <Badge variant="secondary" className="text-[10px]">+{previewIds.length - 40} more</Badge>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="ghost" size="sm" className="text-xs font-bold" onClick={resetBulk}>Cancel</Button>
                <Button size="sm" className="text-xs font-bold" onClick={stageBulk} disabled={previewIds.length === 0}>
                  Stage {previewIds.length} ID{previewIds.length !== 1 ? "s" : ""}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 space-y-1.5">
          <Label className="text-[11px] font-bold text-muted-foreground">Bulk Delete (CSV / Excel)</Label>
          {bulkDeleteStep === "idle" && (
            <>
              <input
                ref={deleteFileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[ 0 ]; if (f) handleBulkDeleteFile(f); e.target.value = ""; }}
              />
              <Button variant="outline" className="gap-1.5 text-xs font-bold w-full text-red-600" onClick={() => deleteFileInputRef.current?.click()} disabled={controlsDisabled || bulkDeleteParsing || atBatchLimit}>
                {bulkDeleteParsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Upload List to Remove
              </Button>
            </>
          )}
          {bulkDeleteStep === "column-select" && (
            <div className="space-y-2 rounded-lg border border-border/60 p-3">
              <Label className="text-[11px] font-bold text-muted-foreground">Which column is the User ID / E_id?</Label>
              <Select value={String(deleteColumnIndex)} onValueChange={(v) => setDeleteColumnIndex(Number(v))}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {deleteColumnHeaders.map((h, i) => <SelectItem key={i} value={String(i)}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-2 pt-1">
                <Button variant="ghost" size="sm" className="text-xs font-bold" onClick={resetBulkDelete}>Cancel</Button>
                <Button size="sm" variant="destructive" className="text-xs font-bold" onClick={() => deleteColumnIndex !== null && buildDeletePreview(deleteColumnIndex)}>
                  Preview
                </Button>
              </div>
            </div>
          )}
          {bulkDeleteStep === "preview" && (
            <div className="space-y-2 rounded-lg border border-border/60 p-3">
              <Label className="text-[11px] font-bold text-muted-foreground">
                Found {previewDeleteIds.length} unique ID{previewDeleteIds.length !== 1 ? "s" : ""} to remove
              </Label>
              <div className="max-h-24 overflow-y-auto flex flex-wrap gap-1">
                {previewDeleteIds.slice(0, 40).map((id) => (
                  <Badge key={id} variant="destructive" className="font-mono text-[10px]">{id}</Badge>
                ))}
                {previewDeleteIds.length > 40 && (
                  <Badge variant="destructive" className="text-[10px]">+{previewDeleteIds.length - 40} more</Badge>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="ghost" size="sm" className="text-xs font-bold" onClick={resetBulkDelete}>Cancel</Button>
                <Button size="sm" variant="destructive" className="text-xs font-bold" onClick={stageBulkDelete} disabled={previewDeleteIds.length === 0}>
                  Stage {previewDeleteIds.length} Removal{previewDeleteIds.length !== 1 ? "s" : ""}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {atBatchLimit && (
        <p className="text-[11px] font-bold text-amber-700">
          {MAX_ACCESS_BATCH_SIZE} changes staged — Apply this batch before staging more.
        </p>
      )}

      {notice && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice(null)} className="shrink-0"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {pendingCount > 0 && (
        <div className="space-y-1.5 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <Label className="text-[11px] font-bold text-amber-800">Pending Changes ({pendingCount})</Label>
          <div className="max-h-28 overflow-y-auto flex flex-wrap gap-1.5">
            {Array.from(pendingGrants).map((id) => (
              <Badge key={`g-${id}`} className="gap-1 bg-green-600 hover:bg-green-600 font-mono text-[10px]">
                <Plus className="h-2.5 w-2.5" /> {id}
                <button onClick={() => unstageGrant(id)}><X className="h-2.5 w-2.5" /></button>
              </Badge>
            ))}
            {Array.from(pendingRevokes).map((id) => (
              <Badge key={`r-${id}`} variant="destructive" className="gap-1 font-mono text-[10px]">
                <Trash2 className="h-2.5 w-2.5" /> {id}
                <button onClick={() => unstageRevoke(id)}><X className="h-2.5 w-2.5" /></button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {pendingClearAll && (
        <div className="flex items-center justify-between rounded-lg border border-red-300 bg-red-50 p-3">
          <p className="text-[11px] font-bold text-red-700">
            Pending: remove all {grantedCount} currently granted {label.toLowerCase()}.
          </p>
          <Button variant="ghost" size="sm" className="h-7 gap-1 text-[11px] font-bold" onClick={undoClearAll}>
            <Undo2 className="h-3 w-3" /> Undo
          </Button>
        </div>
      )}

      {/* Row 3 — table of granted IDs, always visible when there's data */}
      {searchResult ? (
        <div className="rounded-lg border border-border/60 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {searchResult.found
                ? <Check className="h-4 w-4 text-green-600" />
                : <XCircle className="h-4 w-4 text-muted-foreground" />}
              <Badge variant="secondary" className="font-mono text-[11px]">{searchResult.id}</Badge>
              <span className="text-xs text-muted-foreground">
                {searchResult.found ? "currently granted" : "not granted"}
              </span>
            </div>
            {searchResult.found ? (
              <Button variant="ghost" size="sm" className="h-7 gap-1 text-[11px] font-bold text-red-600" onClick={() => stageRevoke(searchResult.id)} disabled={controlsDisabled}>
                <Trash2 className="h-3 w-3" /> Stage Removal
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="h-7 gap-1 text-[11px] font-bold text-green-700" onClick={() => stageGrant(searchResult.id)} disabled={controlsDisabled}>
                <Plus className="h-3 w-3" /> Stage Grant
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-[11px] font-bold text-muted-foreground">
              Granted Access ({grantedCount} total)
            </Label>
            {grantedCount > 0 && (
              <Button variant="ghost" size="sm" className="h-6 gap-1 text-[11px] font-bold text-red-600" onClick={() => setClearAllConfirmOpen(true)} disabled={controlsDisabled}>
                <Trash2 className="h-3 w-3" /> Clear All
              </Button>
            )}
          </div>

          {effectiveMode === "open" && !showEmptyPlaceholder && (
            <div className="flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 text-[11px] text-blue-800">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Access is currently <strong>Open</strong> — the list below exists but isn't being enforced;
                every {label.toLowerCase()} can already self-serve download this template. Turn on Restricted
                Access to start enforcing it.
              </span>
            </div>
          )}

          <div className="flex-1 min-h-[45vh] rounded-lg border border-border/60 overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-border/40">
              {rowsLoading ? (
                <div className="flex items-center justify-center p-6">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : showEmptyPlaceholder ? (
                <p className="text-xs text-muted-foreground p-3">
                  No restricted IDs on file yet. Turn on Restricted Access to start building this list.
                </p>
              ) : rows.length === 0 ? (
                <p className="text-xs text-muted-foreground p-3">No one added yet.</p>
              ) : (
                rows.map((id) => {
                  const stagedForRemoval = pendingRevokes.has(id);
                  return (
                    <div key={id} className={`flex items-center justify-between px-3 py-2 ${stagedForRemoval ? "bg-red-50" : ""}`}>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selected.has(id)}
                          onCheckedChange={() => toggleSelected(id)}
                          disabled={controlsDisabled || stagedForRemoval || (!selected.has(id) && selected.size >= MAX_SELECTION_SIZE)}
                        />
                        <Badge variant="secondary" className={`font-mono text-[11px] ${stagedForRemoval ? "line-through opacity-60" : ""}`}>{id}</Badge>
                        {stagedForRemoval && <span className="text-[10px] font-bold text-red-600">pending removal</span>}
                      </div>
                      {stagedForRemoval ? (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => unstageRevoke(id)} title="Undo">
                          <Undo2 className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => stageRevoke(id)} title="Remove" disabled={controlsDisabled}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className="shrink-0 flex items-center justify-between border-t border-border/40 px-3 py-1.5 bg-muted/20">
              <Button variant="ghost" size="sm" className="text-[11px] font-bold" onClick={goPrev} disabled={pageIndex === 0 || rowsLoading}>
                Previous
              </Button>
              <span className="text-[11px] text-muted-foreground">Page {pageIndex + 1}</span>
              <Button variant="ghost" size="sm" className="text-[11px] font-bold" onClick={goNext} disabled={!hasMore || rowsLoading}>
                Next
              </Button>
            </div>
          </div>

          {selected.size > 0 && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1.5 text-xs font-bold text-red-600" onClick={removeSelected}>
                <Trash2 className="h-3.5 w-3.5" /> Remove Selected ({selected.size}/{MAX_SELECTION_SIZE})
              </Button>
              <Button variant="ghost" size="sm" className="text-[11px] font-bold" onClick={clearSelection}>
                Clear selection
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Row 4 — Apply bar */}
      {hasPendingChanges && (
        <div className="sticky bottom-0 flex items-center justify-between rounded-xl border border-primary/30 bg-background p-3 shadow-md shrink-0">
          <p className="text-xs font-bold">
            {[
              pendingMode !== null && "1 mode change",
              pendingGrants.size > 0 && `${pendingGrants.size} to grant`,
              pendingRevokes.size > 0 && `${pendingRevokes.size} to remove`,
              pendingClearAll && "clear all granted",
            ].filter(Boolean).join(" · ")}
          </p>
          <Button size="sm" className="text-xs font-bold" onClick={() => setApplyConfirmOpen(true)}>
            Review & Apply
          </Button>
        </div>
      )}

      <AlertDialog open={clearAllConfirmOpen} onOpenChange={setClearAllConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove all {grantedCount} granted {label.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>
              This stages a removal of every {label.toLowerCase()} currently granted access to this template.
              Nothing is applied until you review and confirm in the Apply step.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setClearAllConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmClearAll}>Stage Clear All</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={applyConfirmOpen} onOpenChange={(next) => !applying && setApplyConfirmOpen(next)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply changes to {label}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left">
                {pendingMode !== null && (
                  <p>Access mode: <strong>{mode === "open" ? "Open" : "Restricted"}</strong> → <strong>{pendingMode === "open" ? "Open" : "Restricted"}</strong></p>
                )}
                {pendingClearAll && <p className="text-red-700 font-semibold">Remove all {grantedCount} currently granted {label.toLowerCase()}.</p>}
                {!pendingClearAll && pendingGrants.size > 0 && <p><strong>{pendingGrants.size}</strong> {label.toLowerCase()} to be granted access.</p>}
                {!pendingClearAll && pendingRevokes.size > 0 && <p><strong>{pendingRevokes.size}</strong> {label.toLowerCase()} to have access removed.</p>}
                <p className="text-[11px] text-muted-foreground">Sent as a single request.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setApplyConfirmOpen(false)} disabled={applying}>Cancel</Button>
            <Button onClick={applyChanges} disabled={applying} className="gap-1.5">
              {applying && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Confirm & Apply
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function AccessControlPanel({ open, onOpenChange, template, onChanged }: Props) {
  const [ live, setLive ] = useState<DocumentTemplate | null>(template);
  const appliedAnyChange = useRef(false);

  useEffect(() => { setLive(template); appliedAnyChange.current = false; }, [ template ]);

  if (!live) return null;

  // Access-control edits are staged locally and applied in batches — the
  // parent's document list (and its "N granted" badges) only needs to
  // refresh once, when the dialog closes, and only if something actually
  // changed — never on every individual edit made while the dialog is open.
  const handleOpenChange = (next: boolean) => {
    if (!next && appliedAnyChange.current) onChanged();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[100vw] sm:max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] rounded-none p-4 gap-2 flex flex-col overflow-hidden border-border/60">
        <DialogHeader className="border-b border-border/40 pb-2 pr-12 shrink-0">
          <DialogTitle className="text-lg font-bold tracking-tight">Access Control</DialogTitle>
          <p className="text-xs text-muted-foreground">{live.name}</p>
        </DialogHeader>

        <Tabs defaultValue="student" className="flex-1 min-h-0 flex flex-col">
          <TabsList className="shrink-0">
            <TabsTrigger value="student">Students</TabsTrigger>
            <TabsTrigger value="faculty">Faculty</TabsTrigger>
          </TabsList>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <TabsContent value="student" className="h-full">
              <RoleAccessTable
                templateId={live.id}
                role="student"
                initialMode={live.access.student.mode}
                initialCount={live.access.student.allowedIdsCount}
                onApplied={(updated) => { setLive(updated); appliedAnyChange.current = true; }}
              />
            </TabsContent>
            <TabsContent value="faculty" className="h-full">
              <RoleAccessTable
                templateId={live.id}
                role="faculty"
                initialMode={live.access.faculty.mode}
                initialCount={live.access.faculty.allowedIdsCount}
                onApplied={(updated) => { setLive(updated); appliedAnyChange.current = true; }}
              />
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="border-t border-border/40 pt-2 shrink-0">
          <Button className="text-xs font-bold" onClick={() => handleOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
